import SendFilled16 from '@carbon/icons-react/es/send--filled/16';
import { Col } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PermissionSetType, ProjectId, User, UserType } from 'api/models';
import { Select } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { addMembersToProject } from 'data/projects/actions';
import { useCurrentUserProjectAccess } from 'hooks/permissions';
import { useTranslation } from 'hooks/use-translation';
import { NativeInvite } from '..';
import { StyledButton, StyledRow, StyledSelect } from '../common-styles';
import { getFormattedUsersList, getOptions } from '../helpers';

const StyledSelectItem = styled(Select)`
  ${StyledSelect};
`;

export const NativeUserInvitation: FC<NativeInvite> = ({
  allOptions,
  inValidOptions,
  projectId,
  buttonText,
  autoFocus = false,
  project,
}) => {
  const dispatch = useDispatch();
  const [selectedUsers, setSelectedUsers] = React.useState<User[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { isProjectAdmin } = useCurrentUserProjectAccess(ProjectId(projectId));
  const translatedTexts = {
    success: {
      title: useTranslation('invitation.success.title'),
      message: useTranslation('invitation.success.content'),
    },
    error: {
      title: useTranslation('invitation.team_member.error.title'),
      message: useTranslation('invitation.team_member.error.content'),
    },
  };

  const handleSuccess = () => {
    toastService({
      toastContainer: {},
      title: translatedTexts.success.title,
      message: translatedTexts.success.message,
    }).success();

    setSelectedUsers([]);
    setIsLoading(false);
  };

  const handleFailure = () => {
    toastService({
      toastContainer: {},
      title: translatedTexts.error.title,
      message: translatedTexts.error.message,
    }).error();

    setIsLoading(false);
  };

  const handleInvite = () => {
    const invitingMembers = selectedUsers?.map((u) => {
      const permissionSetType = PermissionSetType.Collaborator;

      return {
        user: { userId: u.userId },
        permissionSet: { permissionSetType },
      };
    });

    if (!isEmpty(selectedUsers)) {
      setIsLoading(true);
      dispatch(
        addMembersToProject({
          projectId,
          members: invitingMembers,
          handleSuccess,
          handleFailure,
        }),
      );
    }
  };
  const handleOnChange = (value: User[]) => {
    setSelectedUsers(value || []);
  };

  return (
    <StyledRow>
      <Col flex="auto">
        <StyledSelectItem
          isMulti
          type="user"
          options={getOptions(allOptions, inValidOptions, UserType.Native)}
          value={getFormattedUsersList(selectedUsers)}
          isDisabled={isLoading}
          isLoading={isLoading}
          autoFocus={autoFocus}
          onChange={handleOnChange}
          noOptionsMessage={() => {
            if (isProjectAdmin) {
              return (
                <FormattedMessage
                  id="project.invitation.native.admin.help"
                  values={{
                    a: (chunks) => (
                      <Link to="/settings/team-members">{chunks}</Link>
                    ),
                  }}
                />
              );
            }

            return (
              <FormattedMessage id="project.invitation.native.collaborator.help" />
            );
          }}
          components={{
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
          }}
          closeMenuOnSelect={false}
          startDate={moment(project.startDate)}
          dueDate={moment(project.dueDate)}
        />
      </Col>
      <Col flex="none">
        <StyledButton
          type="primary"
          icon={<SendFilled16 />}
          iconPlacement="right"
          onClick={handleInvite}
          loading={isLoading}
          disabled={isEmpty(selectedUsers)}
          data-cy="native-member-invite-btn"
        >
          {buttonText}
        </StyledButton>
      </Col>
    </StyledRow>
  );
};
